import { Link } from 'react-router-dom';
import { Eyebrow, SectionHeader } from '@/components/Atoms';
import { HALLIE } from '@/data/images';
import useSEO from '@/hooks/useSEO';
import { BUSINESS_SCHEMA } from '@/data/schema';

const VALUES = [
  { title: 'Reliability', body: 'Showing up consistently and following through is the foundation of everything we do.' },
  { title: 'Accountability', body: 'We take ownership of outcomes, not just task lists.' },
  { title: 'Care', body: 'Your clients are the reason your business exists. They are treated that way.' },
  { title: 'Consistency', body: 'Calm, structured systems that perform the same way every week.' },
];

const STEPS = [
  { step: '01', title: 'Inquiry', body: 'You share a little about your business and what is feeling stuck.' },
  { step: '02', title: 'Discovery Call', body: 'A short, focused conversation to understand the shape of the work.' },
  { step: '03', title: 'Recommendation', body: 'A clear, written recommendation, scope, and engagement model tailored to you.' },
  { step: '04', title: 'Proposal', body: 'A plain-English proposal. No surprises.' },
  { step: '05', title: 'Onboarding', body: 'We plug into your systems, shadow your operations, and begin the work.' },
];

export default function Eureka1() {
  useSEO({
    title: 'Eureka1 — Reliable Support, Handled with Care.',
    description:
      'Eureka1 was built to solve a common challenge for service-based businesses — finding dependable help. Through structured systems, accountability, and carefully matched support, Eureka1 helps businesses operate with greater confidence and consistency.',
    image: HALLIE.dark,
    path: '/eureka1',
    schema: BUSINESS_SCHEMA,
  });

  return (
    <div>
      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <Eyebrow>The Practice</Eyebrow>
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-[88px] leading-[0.98] tracking-tight mt-6">
              Reliable Support,<br/>
              <span className="font-serif-italic italic text-[#C9A24D]">Handled with Care.</span>
            </h1>
            <p className="mt-8 text-lg text-[#1C1C1C]/80 leading-relaxed max-w-xl">
              Eureka1 was built to solve a common challenge faced by service-based businesses.
            </p>
            <p className="mt-4 font-serif-italic italic text-2xl text-[#1C1C1C]/85 max-w-xl leading-snug">
              The problem isn&rsquo;t finding help. The problem is finding dependable help.
            </p>
          </div>
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={HALLIE.motion} alt="Hallie — founder of Eureka1" className="w-full h-full object-cover editorial-img" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>Brand Story</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
            Built for the businesses<br/><span className="font-serif-italic italic text-[#C9A24D]">that need support they can rely on.</span>
          </h2>
          <div className="mt-10 space-y-6 text-lg text-[#F4EFEA]/85 leading-relaxed">
            <p>
              Many business owners spend more time managing people, following up on tasks, and fixing communication issues than focusing on growth.
            </p>
            <p>
              Eureka1 exists to reduce that burden.
            </p>
            <p>
              Through structured systems, accountability, and carefully matched support, we help businesses operate with greater confidence and consistency.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div className="card-soft p-10 lg:p-14">
            <Eyebrow>Mission</Eyebrow>
            <h3 className="font-serif-display text-3xl lg:text-[36px] mt-5 leading-tight">
              To help business owners reclaim their time and focus by providing reliable support built on ownership, accountability, and consistency.
            </h3>
          </div>
          <div className="card-soft p-10 lg:p-14">
            <Eyebrow>Vision</Eyebrow>
            <h3 className="font-serif-display text-3xl lg:text-[36px] mt-5 leading-tight">
              To become the most trusted support partner for service-based businesses seeking dependable operational support.
            </h3>
          </div>
        </div>
      </section>

      <section className="bg-[#FBF7F2] border-y border-[#E6CFC7]/70 py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <Eyebrow>Why Eureka1</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
              Most owners don&rsquo;t<br/>struggle because<br/>they lack help.
            </h2>
            <p className="font-serif-italic italic text-2xl text-[#C9A24D] mt-6 leading-snug">
              They struggle because the help isn&rsquo;t reliable.
            </p>
          </div>
          <div className="lg:col-span-7 space-y-6 text-[#1C1C1C]/85 leading-relaxed text-lg lg:pt-3">
            <p>
              Our focus isn&rsquo;t simply task completion.
            </p>
            <p>
              Our focus is creating dependable support systems that reduce mental load and improve operational consistency.
            </p>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <SectionHeader eyebrow="Core Values" title="What we hold" italic="ourselves to." />
          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-px bg-[#E6CFC7]/60">
            {VALUES.map((v, i) => (
              <div key={v.title} className="bg-[#F4EFEA] p-8">
                <div className="num-marker">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-serif-display text-2xl mt-5">{v.title}</h3>
                <p className="text-[#1C1C1C]/75 leading-relaxed mt-3 text-sm">{v.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
          <Eyebrow>How It Works</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight max-w-3xl">
            From first inquiry<br/><span className="font-serif-italic italic text-[#C9A24D]">to dependable support.</span>
          </h2>
          <ol className="mt-14 border-t border-[#F4EFEA]/15">
            {STEPS.map((s) => (
              <li key={s.step} className="grid grid-cols-12 gap-6 py-8 border-b border-[#F4EFEA]/15 items-baseline">
                <div className="col-span-12 sm:col-span-2 num-marker text-[#C9A24D]">{s.step}</div>
                <div className="col-span-12 sm:col-span-3">
                  <h3 className="font-serif-display text-2xl">{s.title}</h3>
                </div>
                <div className="col-span-12 sm:col-span-7 text-[#E6CFC7]/85 leading-relaxed text-lg">{s.body}</div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <div className="aspect-[4/5] overflow-hidden">
              <img src={HALLIE.dark} alt="Hallie — Founder of Eureka1" className="w-full h-full object-cover editorial-img" />
            </div>
          </div>
          <div className="lg:col-span-7">
            <Eyebrow>Founder Story</Eyebrow>
            <h2 className="font-serif-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight">
              Built from observing<br/><span className="font-serif-italic italic text-[#C9A24D]">the same challenge, repeatedly.</span>
            </h2>
            <div className="mt-10 space-y-6 text-lg text-[#1C1C1C]/85 leading-relaxed">
              <p>
                Hallie founded Eureka1 after working closely with business owners and observing the same challenge repeatedly.
              </p>
              <p>The issue wasn&rsquo;t talent. The issue was reliability, accountability, and ownership.</p>
              <p>
                Missed follow-ups, inconsistent communication, and the need for constant supervision created unnecessary stress and limited growth.
              </p>
              <p className="font-serif-italic italic text-[#1C1C1C]">
                Eureka1 was built to provide a different experience — one rooted in trust, structure, and dependable execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-[#1C1C1C] text-[#F4EFEA] py-24 lg:py-32 text-center">
        <div className="max-w-[1000px] mx-auto px-6">
          <Eyebrow className="mx-auto">Begin</Eyebrow>
          <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05] tracking-tight">
            Curious whether Eureka1<br/><span className="font-serif-italic italic text-[#C9A24D]">is the right fit?</span>
          </h2>
          <div className="mt-10">
            <Link to="/contact" className="btn-ghost-light" data-testid="eureka1-cta-book-call">Book a Discovery Call</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
